import { useMemo } from 'react';
import useStdQuery from 'components/data/hooks/useStdQuery.js';
import PublicAppVars from 'utils/PublicAppVars.js';
import { gql } from '@apollo/client';


export const FUNDING_SOURCES_GET_QUERY = gql`
	query GET_FundingSources {
		CustomerRoute {
			id
			getFundingSources {
				id
				directDebitFundingSourceCountRemaining
				creditCardFundingSourceCountRemaining
				directDebitFundingSourceAttemptsCountRemaining

				fundingSources {
					id
					billingAddressId
					fundingSourceId
					nickname
					isBlocked
					setAsPrimary
					status
					statusDescription
					creditCard {
						id
						cardExpiryMMYY
						creditCardType
						maskedPan
						pgCardId
						nameOnCard
					}
					directDebit {
						id
						financialInstitutionName
						#maskedBankAccountNumber
						accountType
					}
				}
			}
		}
	}
`;

// We can only do a "soft" delete, payment methods just get assigned a deleted status.
// Filter out deleted cards.
export const filterDeleted = fundingSources => fundingSources?.filter(wsFundingSourceExt => wsFundingSourceExt.status !== "Deleted");

export const useFundingSources = () => {
	const response = useStdQuery(FUNDING_SOURCES_GET_QUERY);

	const {
		fundingSources,
		fundingSourceCountRemaining,
		creditCardFundingSourceCountRemaining,
		directDebitFundingSourceCountRemaining,
		directDebitFundingSourceAttemptsCountRemaining,
	} = response?.data?.CustomerRoute?.getFundingSources ?? {};

	const wsFundingSourceInfos = filterDeleted(fundingSources);

	return {
		...response,
		wsFundingSourceInfos,
		fundingSourceCountRemaining,
		creditCardFundingSourceCountRemaining,
		directDebitFundingSourceCountRemaining,
		directDebitFundingSourceAttemptsCountRemaining,
	};
};

const convertMMYY_toDateTime = cardExpiryMMYY => {
	let month = parseInt(`${cardExpiryMMYY[ 0 ]}${cardExpiryMMYY[ 1 ]}`);
	let year = parseInt(`20${cardExpiryMMYY[ 2 ]}${cardExpiryMMYY[ 3 ]}`);

	// Increment the month as we want to validate against the very beginning of the next month.
	// If we are at the end of the year then set to 1/year +1.
	if (month === 12) {
		month = 1;
		year++;
	}
	else {
		month++;
	}

	return new Date(`${year}-${month}-1`);
};

// Returns funding Sources that are expired or will be expired
export const useExpiringFundingSources = () => {
	const { wsFundingSourceInfos } = useFundingSources(); 	// Return all non-deleted fundingSources
	const today = new Date(); // Always the same during a render

	const expiredFundingSources = useMemo(() =>
		wsFundingSourceInfos?.filter(source => {
			if (source.creditCard?.cardExpiryMMYY) {
				const convertedDate = convertMMYY_toDateTime(source.creditCard?.cardExpiryMMYY);

				return today.getTime() > convertedDate.getTime();
			}
		// eslint-disable-next-line react-hooks/exhaustive-deps
		}) ?? [], [  wsFundingSourceInfos ]);

	const expiringFundingSources = useMemo(() => wsFundingSourceInfos?.filter(source => {
		if (source.creditCard?.cardExpiryMMYY) {
			const convertedDate = convertMMYY_toDateTime(source.creditCard?.cardExpiryMMYY);

			// Card is already expired
			if (today.getTime() > convertedDate.getTime()) {
				return false;
			}

			const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
			const daysTilExpired = Math.round(Math.abs((convertedDate - today) / oneDay));

			// We only want the funding sources between the window of 0 days and Nth days.
			return daysTilExpired > 0 && daysTilExpired <= PublicAppVars.EXPIRING_FUNDING_SOURCE_DAY_COUNT;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}) ?? [], [ wsFundingSourceInfos ]);

	return {
		expiredFundingSources,
		expiringFundingSources,
	};
};
